import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Forum as ForumIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../api';

const CHANNEL_FILTERS = [
  { value: '', label: 'All channels' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram_dm', label: 'Instagram DM' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'in_app', label: 'In-app' },
  { value: 'manual_other', label: 'Other' },
];

const EVENT_TYPE_FILTERS = [
  { value: '', label: 'All event types' },
  { value: 'message_manually_sent', label: 'Marked as sent' },
  { value: 'draft_created', label: 'Draft created' },
  { value: 'links_generated', label: 'Links generated' },
  { value: 'message_copied', label: 'Message copied' },
  { value: 'portal_link_opened', label: 'Portal link opened' },
  { value: 'maintenance_request_created', label: 'Maintenance submitted' },
  { value: 'access_response_submitted', label: 'Access response' },
  { value: 'document_viewed', label: 'Document viewed' },
];

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const labelForType = (type) => {
  const match = EVENT_TYPE_FILTERS.find((t) => t.value === type);
  return match?.label || type || 'Event';
};

/**
 * ZRF-WTPB-008 — Communication timeline viewer.
 * GET /api/communications/timeline with property/unit/tenant/channel/type filters.
 */
const CommunicationsTimelinePage = ({ onNewMessage }) => {
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [channel, setChannel] = useState('');
  const [eventType, setEventType] = useState('');
  const [events, setEvents] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [metaError, setMetaError] = useState('');
  const [eventsError, setEventsError] = useState('');

  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === propertyId) || null,
    [properties, propertyId]
  );

  const units = useMemo(() => {
    if (!selectedProperty) return [];
    return Array.isArray(selectedProperty.units) ? selectedProperty.units : [];
  }, [selectedProperty]);

  const tenantsForScope = useMemo(() => {
    if (!propertyId) return tenants;
    return tenants.filter((t) => {
      const leases = Array.isArray(t.leases) ? t.leases : [];
      if (leases.length === 0) {
        return !t.propertyId || t.propertyId === propertyId;
      }
      return leases.some((lease) => {
        if (lease.propertyId !== propertyId) return false;
        if (unitId && lease.unitId && lease.unitId !== unitId) return false;
        return true;
      });
    });
  }, [tenants, propertyId, unitId]);

  const propertyNameById = useMemo(() => {
    const map = {};
    properties.forEach((p) => {
      map[p.id] = p.name || p.address || p.id;
    });
    return map;
  }, [properties]);

  const unitNameById = useMemo(() => {
    const map = {};
    properties.forEach((p) => {
      (Array.isArray(p.units) ? p.units : []).forEach((u) => {
        map[u.id] = u.name || u.unitNumber || u.id;
      });
    });
    return map;
  }, [properties]);

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => {
      map[t.id] = [t.firstName, t.lastName].filter(Boolean).join(' ') || t.name || null;
    });
    return map;
  }, [tenants]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingMeta(true);
      setMetaError('');
      try {
        const [propsRes, tenantsRes] = await Promise.all([
          api.get('/api/properties'),
          api.get('/api/tenants').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const props = Array.isArray(propsRes.data)
          ? propsRes.data
          : propsRes.data?.properties || [];
        setProperties(props);
        const tenantList = Array.isArray(tenantsRes.data)
          ? tenantsRes.data
          : tenantsRes.data?.tenants || [];
        setTenants(tenantList);
        console.log('✅ Communications timeline meta loaded', {
          properties: props.length,
          tenants: tenantList.length,
        });
      } catch (err) {
        console.error('❌ Timeline meta load failed:', err);
        if (!cancelled) {
          setMetaError('Could not load properties for filters.');
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setEventsError('');
    try {
      const params = { limit: 50 };
      if (propertyId) params.propertyId = propertyId;
      if (unitId) params.unitId = unitId;
      if (tenantId) params.tenantId = tenantId;
      if (channel) params.channel = channel;
      if (eventType) params.type = eventType;

      const res = await api.get('/api/communications/timeline', { params });
      const list = Array.isArray(res.data?.events) ? res.data.events : [];
      setEvents(list);
      console.log('✅ Timeline events loaded', { count: list.length, params });
    } catch (err) {
      console.error('❌ Timeline events load failed:', err);
      const status = err?.response?.status;
      setEvents([]);
      setEventsError(
        status === 404
          ? 'Communications bridge is off on the API, or timeline is unavailable (404).'
          : err?.response?.data?.error ||
              err?.message ||
              'Could not load timeline events.'
      );
    } finally {
      setLoadingEvents(false);
    }
  }, [propertyId, unitId, tenantId, channel, eventType]);

  useEffect(() => {
    if (loadingMeta) return undefined;
    loadEvents();
    return undefined;
  }, [loadingMeta, loadEvents]);

  if (loadingMeta) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Communications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review logged sends and portal activity. WhatsApp stays the conversation;
            ZenRentFlow keeps the trail.
          </Typography>
        </Box>
        <Button
          onClick={onNewMessage}
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, whiteSpace: 'nowrap' }}
        >
          New Message
        </Button>
      </Stack>

      {metaError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {metaError}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <ForumIcon color="primary" />
          <Typography variant="h6" component="h2">
            Timeline
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filters
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2 }}
          useFlexGap
          flexWrap="wrap"
        >
          <FormControl sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel id="tl-property-label">Property</InputLabel>
            <Select
              labelId="tl-property-label"
              label="Property"
              value={propertyId}
              onChange={(e) => {
                setPropertyId(e.target.value);
                setUnitId('');
                setTenantId('');
              }}
            >
              <MenuItem value="">
                <em>All accessible</em>
              </MenuItem>
              {properties.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name || p.address || p.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, flex: 1 }} disabled={!propertyId || units.length === 0}>
            <InputLabel id="tl-unit-label">Unit</InputLabel>
            <Select
              labelId="tl-unit-label"
              label="Unit"
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
                setTenantId('');
              }}
            >
              <MenuItem value="">
                <em>All units</em>
              </MenuItem>
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name || u.unitNumber || u.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 160, flex: 1 }} disabled={!propertyId}>
            <InputLabel id="tl-tenant-label">Tenant</InputLabel>
            <Select
              labelId="tl-tenant-label"
              label="Tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <MenuItem value="">
                <em>All tenants</em>
              </MenuItem>
              {tenantsForScope.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, flex: 1 }}>
            <InputLabel id="tl-channel-label">Channel</InputLabel>
            <Select
              labelId="tl-channel-label"
              label="Channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              {CHANNEL_FILTERS.map((c) => (
                <MenuItem key={c.value || 'all'} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel id="tl-type-label">Event type</InputLabel>
            <Select
              labelId="tl-type-label"
              label="Event type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              {EVENT_TYPE_FILTERS.map((t) => (
                <MenuItem key={t.value || 'all'} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Button
          size="small"
          startIcon={loadingEvents ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={loadEvents}
          disabled={loadingEvents}
          sx={{ mb: 2 }}
        >
          Refresh
        </Button>

        {eventsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {eventsError}
          </Alert>
        )}

        {loadingEvents && events.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : events.length === 0 ? (
          <Alert severity="info">
            No timeline events yet. Compose a message, generate links, and mark as sent to create
            the first entry.
          </Alert>
        ) : (
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {events.map((event) => (
              <Box key={event.id} sx={{ py: 0.5 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={0.5}
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="subtitle1" component="h3">
                    {event.title || labelForType(event.type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatWhen(event.createdAt)}
                  </Typography>
                </Stack>
                {event.summary ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {event.summary}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {event.type ? (
                    <Chip size="small" label={labelForType(event.type)} variant="outlined" />
                  ) : null}
                  {event.channel ? (
                    <Chip size="small" label={event.channel} color="primary" variant="outlined" />
                  ) : null}
                  {event.propertyId ? (
                    <Chip
                      size="small"
                      label={propertyNameById[event.propertyId] || event.propertyId}
                      variant="outlined"
                    />
                  ) : null}
                  {event.unitId ? (
                    <Chip
                      size="small"
                      label={`Unit ${unitNameById[event.unitId] || event.unitId}`}
                      variant="outlined"
                    />
                  ) : null}
                  {event.tenantId ? (
                    <Chip
                      size="small"
                      label={tenantNameById[event.tenantId] || `Tenant ${event.tenantId.slice(0, 8)}…`}
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default CommunicationsTimelinePage;
